// Minicart controller (vanilla ES module)
// Provides: window.minicart.open(payload), .close(), .init(), .setInvoker(el)

// Ensure the global API object exists early
window.minicart = window.minicart || {};

const state = {
  root: null,
  overlay: null,
  modal: null,
  // Mobile sheet elements
  sheet: null,
  sheetBackdrop: null,
  sheetHandle: null,
  closeBtn: null,
  titleEl: null,
  imageEl: null,
  productEl: null,
  priceEl: null,
  variantEl: null,
  countEl: null,
  viewCartBtn: null,
  viewCartBtnMobile: null,
  autoCloseTimer: null,
  invoker: null,
  initialized: false,
  relatedSwiper: null,
  scrollY: 0,
};

// Close automatically if the user doesn't interact with the minicart
const AUTO_CLOSE_MS = 10000;
const INTERACTION_EVENTS = ["pointerdown", "pointerenter", "touchstart", "wheel", "keydown"];

function startAutoClose(closeFn) {
  clearTimeout(state.autoCloseTimer);
  state.autoCloseTimer = setTimeout(closeFn, AUTO_CLOSE_MS);
}

function cancelAutoClose() {
  clearTimeout(state.autoCloseTimer);
  state.autoCloseTimer = null;
}

// Any interaction keeps the minicart open until the user closes it
function bindInteractionCancel(el) {
  if (!el || el.dataset.minicartAutoCloseBound) return;
  el.dataset.minicartAutoCloseBound = "true";
  INTERACTION_EVENTS.forEach((type) =>
    el.addEventListener(type, cancelAutoClose, { passive: true }),
  );
}

function lockScroll() {
  state.scrollY = window.scrollY;
  document.body.style.top = `-${state.scrollY}px`;
  document.body.classList.add("no-scroll");
}

function unlockScroll() {
  document.body.classList.remove("no-scroll");
  document.body.style.top = "";
  window.scrollTo({ top: state.scrollY, behavior: "instant" });
}

function initMinicart() {
  if (state.initialized) return;

  state.root = document.querySelector(".brn-minicart");
  if (!state.root) {
    // @note PDP should render the snippet; if not present, gracefully no‑op
    console.warn("[minicart] snippet not found on the page");
    return;
  }

  state.overlay = state.root.querySelector(".brn-minicart__overlay");
  state.modal = state.root.querySelector(".brn-minicart__modal");
  // Mobile sheet refs
  state.sheet = document.getElementById("minicart-sheet");
  // Backdrop puede ser hermano del root; selecciónalo a nivel de documento
  state.sheetBackdrop = document.querySelector("[data-minicart-backdrop]");
  state.sheetHandle = state.sheet?.querySelector("[data-toggle]") || null;
  state.closeBtn = state.root.querySelector("[data-modal-close]");
  state.titleEl = state.root.querySelector(".brn-minicart__title");
  state.imageEl = state.root.querySelector(".brn-minicart__image");
  state.productEl = state.root.querySelector(".brn-minicart__product");
  state.priceEl = state.root.querySelector(".brn-minicart__price");
  state.variantEl = state.root.querySelector(".brn-minicart__variant");
  state.countEl = state.root.querySelector(".brn-minicart__count");
  state.viewCartBtn = state.root.querySelector(".brn-minicart__view-cart");
  // Mobile button might be in the sheet
  state.viewCartBtnMobile = state.sheet
    ? state.sheet.querySelector(".brn-minicart__view-cart")
    : null;

  // Close interactions
  if (state.overlay) state.overlay.addEventListener("click", close);
  if (state.closeBtn) state.closeBtn.addEventListener("click", close);
  if (state.sheetBackdrop)
    state.sheetBackdrop.addEventListener("click", closeMobileSheet);
  document.addEventListener("keydown", (ev) => {
    const isDesktopActive =
      state.root && state.root.classList.contains("is--active");
    const isMobileActive = state.sheet && state.sheet.dataset.state === "open";
    if (ev.key === "Escape" && (isDesktopActive || isMobileActive)) {
      if (isMobileActive) closeMobileSheet();
      else close();
    }
  });

  // View cart: open drawer if present, else go to cart page
  const handleViewCart = (e) => {
    e.preventDefault();
    if (e.target) e.target.blur();

    const trigger = document.querySelector(".wt-cart__trigger");
    if (trigger) {
      // If drawer exists (trigger present), click it to open
      try {
        trigger.click();
      } catch {
        /* noop */
      }
    } else {
      // Fallback if no drawer trigger found
      const cartUrl = (window.routes && window.routes.cart_url) || "/cart";
      window.location.href = cartUrl;
    }
    // Close minicart after action
    if (state.sheet && state.sheet.dataset.state === "open") closeMobileSheet();
    else close();
  };

  if (state.viewCartBtn)
    state.viewCartBtn.addEventListener("click", handleViewCart);
  if (state.viewCartBtnMobile)
    state.viewCartBtnMobile.addEventListener("click", handleViewCart);

  state.initialized = true;
}

function openMobileSheet() {
  // Ensure initialization and resolve late-inserted refs
  if (!state.initialized) initMinicart();
  state.sheet = state.sheet || document.getElementById("minicart-sheet");
  state.sheetBackdrop =
    state.sheetBackdrop || document.querySelector("[data-minicart-backdrop]");
  if (!state.sheet) return;
  // Ensure desktop overlay is not visible
  if (state.root && state.root.classList.contains("is--active")) close();
  // Open mobile sheet
  // Quitar atributo hidden explícitamente para vencer la regla CSS [hidden]{display:none}
  state.sheet.removeAttribute("hidden");
  // Set closed state first so the entry animation can run
  state.sheet.dataset.state = "closed";
  // Force reflow to ensure the next state change is animated
  void state.sheet.offsetHeight;
  // Now open (will animate from translateY(100%) to 0)
  state.sheet.dataset.state = "open";
  state.sheet.setAttribute("aria-expanded", "true");
  state.sheet.setAttribute("aria-modal", "true");
  lockScroll();
  if (state.sheetBackdrop) {
    state.sheetBackdrop.removeAttribute("hidden");
    state.sheetBackdrop.setAttribute("data-visible", "true");
  }
  // The Permut chat launcher sits at a higher z-index than this sheet
  // ($z-modal, 60) and would otherwise float on top of .brn-minicart__footer.
  if (typeof setChatWidgetHidden === "function") {
    setChatWidgetHidden(true, "minicart-sheet");
  }
  // Initialize related products carousel inside the mobile sheet (deferred script)
  try {
    const carouselEl =
      state.sheet &&
      state.sheet.querySelector("#completeLookMinicartMobileCarousel");
    if (carouselEl && !carouselEl.swiper) {
      const nextEl = state.sheet.querySelector(".brn-carousel__nav--next");
      const prevEl = state.sheet.querySelector(".brn-carousel__nav--prev");
      function initSwiperWhenReady(attempts) {
        if (!carouselEl || carouselEl.swiper) return;
        const SwiperCtor = window.Swiper || window["Swiper"];
        if (typeof SwiperCtor === "function") {
          carouselEl.swiper = new SwiperCtor(carouselEl, {
            slidesPerView: 2.5,
            spaceBetween: 2,
            watchOverflow: true,
            navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
          });
          return;
        }
        if (attempts > 40) return;
        setTimeout(() => initSwiperWhenReady(attempts + 1), 50);
      }
      initSwiperWhenReady(0);
      window.addEventListener("load", () => initSwiperWhenReady(0), {
        once: true,
      });
    }
  } catch (e) {
    console.error("[minicart] mobile swiper init failed", e);
  }
  bindInteractionCancel(state.sheet);
  startAutoClose(closeMobileSheet);
}

function closeMobileSheet() {
  if (!state.initialized) initMinicart();
  state.sheet = state.sheet || document.getElementById("minicart-sheet");
  state.sheetBackdrop =
    state.sheetBackdrop || document.querySelector("[data-minicart-backdrop]");
  if (!state.sheet) return;
  cancelAutoClose();
  state.sheet.dataset.state = "closed";
  state.sheet.setAttribute("aria-expanded", "false");
  state.sheet.setAttribute("aria-modal", "false");
  unlockScroll();
  if (typeof setChatWidgetHidden === "function") {
    setChatWidgetHidden(false, "minicart-sheet");
  }
  // Emit closed event for external flows (e.g., product-form re-open size sheet)
  try {
    window.dispatchEvent(new CustomEvent("minicart:closed"));
  } catch {}
  if (state.sheetBackdrop) {
    state.sheetBackdrop.removeAttribute("data-visible");
    // Hide after fade-out
    setTimeout(() => {
      if (!state.sheetBackdrop.getAttribute("data-visible"))
        state.sheetBackdrop.setAttribute("hidden", "");
    }, 320);
  }
  // Hide mobile sheet after backdrop fade
  setTimeout(() => {
    state.sheet.setAttribute("hidden", "");
  }, 320);
}

function open(payload = {}) {
  if (!state.initialized) initMinicart();
  if (!state.root) return; // graceful no-op

  const isMobile = window.matchMedia("(max-width: 810px)").matches;

  const {
    title, // e.g., "Talla XS añadida a la cesta"
    productTitle,
    imageSrc,
    imageAlt,
    priceText,
    variantText, // e.g., size or color
    itemCount,
  } = payload;

  if (state.titleEl && typeof title === "string")
    state.titleEl.textContent = title;
  if (state.productEl && typeof productTitle === "string")
    state.productEl.textContent = productTitle;
  if (state.priceEl && typeof priceText === "string")
    state.priceEl.textContent = priceText;
  if (state.variantEl && typeof variantText === "string")
    state.variantEl.textContent = variantText;

  if (state.imageEl) {
    if (typeof imageSrc === "string") state.imageEl.src = imageSrc;
    if (typeof imageAlt === "string") state.imageEl.alt = imageAlt;
  }

  if (state.countEl) {
    if (typeof itemCount === "number") {
      state.countEl.textContent = String(itemCount);
    } else {
      // Fallback: fetch cart count if not provided
      try {
        fetch("/cart.js")
          .then((r) => r.json())
          .then((cart) => {
            if (cart && typeof cart.item_count === "number") {
              state.countEl.textContent = String(cart.item_count);
            }
          })
          .catch(() => {});
      } catch {
        /* noop */
      }
    }
  }

  // Mobile: open bottom sheet variant and bail out (do not gate on state.sheet)
  if (isMobile) {
    // Ensure the sheet opens and refs resolve even if init was late
    openMobileSheet();
    const sheetEl = state.sheet || document.getElementById("minicart-sheet");
    // Update mobile-specific elements inside the sheet
    const mobileTitleEl = sheetEl?.querySelector(".brn-minicart__title");
    const mobileCountEl = sheetEl?.querySelector(".brn-minicart__count");
    if (mobileTitleEl && typeof title === "string")
      mobileTitleEl.textContent = title;
    if (mobileCountEl) {
      if (typeof itemCount === "number")
        mobileCountEl.textContent = String(itemCount);
      else if (typeof state.countEl?.textContent === "string")
        mobileCountEl.textContent = state.countEl.textContent;
    }
    // Emit event and return to avoid desktop overlay
    try {
      window.dispatchEvent(
        new CustomEvent("minicart:open", { detail: payload }),
      );
    } catch {}
    return;
  }

  // Desktop: Make overlay + modal visible
  state.root.classList.add("is--active");
  if (state.overlay) state.overlay.classList.add("is--active");
  if (state.modal) state.modal.classList.add("is--active");
  lockScroll();

  // Init related products carousel inside minicart (once)
  try {
    const carouselEl =
      state.modal && state.modal.querySelector("#completeLookMinicartCarousel");
    if (carouselEl && !state.relatedSwiper) {
      const slidesCount = carouselEl.querySelectorAll(".swiper-slide").length;
      if (slidesCount > 0 && typeof Swiper !== "undefined") {
        const nextEl = state.modal.querySelector(".brn-carousel__nav--next");
        const prevEl = state.modal.querySelector(".brn-carousel__nav--prev");
        state.relatedSwiper = new Swiper(carouselEl, {
          slidesPerView: 2.85,
          spaceBetween: 4,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
          navigation: {
            nextEl,
            prevEl,
          },
        });
      }
    }
  } catch (e) {
    console.error("[minicart] failed to init related carousel", e);
  }

  // Focus management: move focus to close button
  const previouslyFocused = document.activeElement;
  if (previouslyFocused && previouslyFocused instanceof HTMLElement) {
    state.invoker = state.invoker || previouslyFocused;
  }
  if (state.closeBtn) state.closeBtn.focus({ preventScroll: true });

  bindInteractionCancel(state.modal);
  startAutoClose(close);

  // Optional: emit event for other UI to react (header badge, etc.)
  try {
    window.dispatchEvent(new CustomEvent("minicart:open", { detail: payload }));
  } catch {
    // ignore
  }
}

function close() {
  if (!state.root) return;
  cancelAutoClose();

  state.root.classList.remove("is--active");
  if (state.overlay) state.overlay.classList.remove("is--active");
  if (state.modal) state.modal.classList.remove("is--active");
  unlockScroll();

  // Optional: keep swiper instance; do not destroy to prevent re-init cost

  // Restore focus to invoker if possible
  if (state.invoker && state.invoker instanceof HTMLElement) {
    try {
      state.invoker.focus({ preventScroll: true });
    } catch {
      /* noop */
    }
    state.invoker = null;
  }

  // Optional: emit event for other UI to react
  try {
    window.dispatchEvent(new CustomEvent("minicart:close"));
  } catch {
    // ignore
  }
}

function setInvoker(el) {
  if (el && el instanceof HTMLElement) state.invoker = el;
}

// Expose a lightweight API for other modules
window.minicart.init = initMinicart;
window.minicart.open = open;
window.minicart.close = close;
// Expose mobile helpers to aid debugging if needed
window.minicart.closeMobile = closeMobileSheet;
window.minicart.openMobile = openMobileSheet;
window.minicart.setInvoker = setInvoker;

// Auto-init on ready
document.addEventListener("DOMContentLoaded", initMinicart);

export { initMinicart, open, close, setInvoker };
